import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {firstValueFrom} from 'rxjs';

// type-only: ApiResult.ts imports this service, so a value import here would be a module cycle
import type {ApiResult} from '../_helpers/ApiResult';

const NONCE_LENGTH = 12;
const TAG_LENGTH = 16;

/**
 * Decodes response payloads the server has encoded for this session.
 *
 * The server encodes `results` for student callers, and for any endpoint it marks as protected, so
 * the data cannot be lifted straight out of the browser's network tab. This is friction, not confidentiality - the browser has to be able to read
 * the payload to render it. Its value is that reading the data becomes a deliberate act rather than
 * a copy and paste.
 *
 * The key lives in the session token, which never leaves the server, so it is fetched once per
 * session from /api/user/payload-key and held in memory only - deliberately not in storage, where
 * it would outlive the page and be trivial to find.
 */
@Injectable({providedIn: 'root'})
export class PayloadProtectionService {

    private static _instance: PayloadProtectionService | null = null;

    static get current(): PayloadProtectionService | null {
        return PayloadProtectionService._instance;
    }

    private keyPromise: Promise<CryptoKey> = null;

    constructor(private readonly http: HttpClient) {
        PayloadProtectionService._instance = this;
    }

    /**
     * Decodes an encoded `results` string back into the object the caller expects.
     */
    async decode(encoded: string): Promise<unknown> {
        const key = await this.sessionKey();

        // Four different things can fail in here and they need telling apart: in QA the symptom
        // of any of them is an empty screen, and the remedies are not the same.
        let raw: Uint8Array;
        try {
            // atob yields one latin-1 char per byte, so it has to go through Uint8Array -
            // treating it as a string corrupts every non-ascii character in the payload.
            raw = Uint8Array.from(atob(encoded), character => character.charCodeAt(0));
        } catch {
            throw new Error('The protected response was not valid base64.');
        }

        if (raw.length <= NONCE_LENGTH + TAG_LENGTH) {
            throw new Error('The protected response is too short to hold a nonce and tag.');
        }

        let plain: ArrayBuffer;
        try {
            const nonce = raw.slice(0, NONCE_LENGTH);
            const body = raw.slice(NONCE_LENGTH);   // ciphertext || tag, the layout AES-GCM expects

            plain = await crypto.subtle.decrypt({name: 'AES-GCM', iv: nonce}, key, body);
        } catch {
            //wrong key or altered bytes look identical here - GCM fails the tag either way
            throw new Error('The protected response could not be decrypted. The session key may have changed - sign in again.');
        }

        try {
            return JSON.parse(new TextDecoder().decode(plain));
        } catch {
            throw new Error('The protected response did not contain valid JSON.');
        }
    }

    private sessionKey(): Promise<CryptoKey> {
        if (this.keyPromise == null) {
            this.keyPromise = this.loadSessionKey()
                .catch(error => {
                    // let the next response try again rather than poisoning the session
                    this.keyPromise = null;
                    throw error;
                });
        }

        return this.keyPromise;
    }

    private async loadSessionKey(): Promise<CryptoKey> {
        const response = await firstValueFrom(
            this.http.get<ApiResult<string>>('/api/user/payload-key')
        );

        const encodedKey = response?.results as string;
        if (!encodedKey) {
            throw new Error('No payload key was issued for this session');
        }

        const keyBytes = Uint8Array.from(atob(encodedKey), character => character.charCodeAt(0));

        return crypto.subtle.importKey('raw', keyBytes, 'AES-GCM', false, ['decrypt']);
    }
}
